import * as React from 'react';
import { Text, View } from 'react-native';
import moment from 'moment';
import momentTimeZone from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { styles } from 'styles/components/orderDetailsData';
import OrderContext from 'contexts/OrderContext';
import useMediumScreen from 'hooks/useMediumScreen';
import useResponsiveContainer from 'hooks/useResponsiveContainer';
import { useFormatMoneyAuto } from 'hooks/useFormatMoneyAuto';
import { TouchableOpacity } from 'react-native';
import OrderDetailsDataProps from 'types/components/OrderDetailsDataProps';
import ModalData from 'types/models/ModalData';
import { SupportedLanguages } from 'types/enums/LanguageType';
import CustomModal from './CustomModal';
import parseAmounts from 'utils/parseAmounts';
import Order from 'types/models/Order';
import { parseSplitPaymentText } from 'utils/report';

export default ({ order, isParkingOrder, parkingVehicleData }: OrderDetailsDataProps): JSX.Element => {
  const { i18n, t } = useTranslation();
  const { currentVenue } = React.useContext(OrderContext);
  const [commentModal, setCommentModal] = React.useState<ModalData | null>(null);
  const mediumScreen = useMediumScreen();
  const formattedCreatedDate = momentTimeZone(order?.localDateTime).format('DD/MM/YYYY HH:mm');
  const formattedEventStartDate = moment(order?.event?.eventStart, ['LT', moment.ISO_8601]).format('DD/MM/YYYY HH:mm');
  const ResponsiveContainer = useResponsiveContainer();

  if (!order) return <></>;

  const orderDiscount = order.orderDetails?.find((detail) => detail.discount);
  const formattedTotalAmount = useFormatMoneyAuto(order.totalAmount);

  const openCommentsModal = () => {
    setCommentModal({
      description: t('comments'),
      action: () => console.log('empty'),
      generalColor: currentVenue?.appColor ?? '#001C47',
    });
  };


  const getPaymentMethodText = (orderParam: Order): string => {
    if (orderParam.payment?.isSplitPayment) {
      return parseSplitPaymentText(orderParam.payment.subPayments, t, i18n.language as SupportedLanguages);
    } else {
      if (order.isCash) return t('cash');
      if (order.isDiscount) return t('discount-order');

      return t('card');
    }
  }

  return (
    <ResponsiveContainer style={mediumScreen ? styles.orderContainerLarge : styles.orderContainerSmall}>
      <View style={mediumScreen ? styles.orderProductsLarge : styles.orderProductsSmall}>
        <Text style={mediumScreen ? styles.sectionContainerTitleLarge : styles.sectionContainerTitleSmall}>
          {t('order-summary')}
        </Text>
        <View style={mediumScreen ? styles.productsContainerLarge : styles.productsContainerSmall}>
          {order.orderDetails?.map((orderDetail, index) => {
            if (!orderDetail.product) return null;
            if (typeof orderDetail.product === 'string') return null;
            return (
              <View
                key={orderDetail.product.id}
                style={[mediumScreen ? styles.rowLarge : styles.rowSmall, index > 0 && { marginTop: '3%' }]}
              >
                <Text style={styles.itemTextProduct}>{`${orderDetail.product.name} ${orderDetail.quantity > 1 ? `(${orderDetail.quantity})` : ''
                  }`}</Text>

                <Text style={styles.itemTextProduct}>{`${parseAmounts(
                  orderDetail.totalAmount,
                  i18n.language as SupportedLanguages,
                )}`}</Text>
              </View>
            );
          })}
          {order.isDiscount && (
            <View key={orderDiscount?.discount?.id} style={mediumScreen ? styles.rowLarge : styles.rowSmall}>
              <Text style={styles.itemTextProduct}>{`${orderDiscount?.discount?.amount}%`}</Text>
            </View>
          )}
          <View style={mediumScreen ? styles.rowTotalLarge : styles.rowTotalSmall}>
            <Text style={[styles.itemTextTotalProduct, { color: currentVenue?.appColor ?? '#001C47' }]}>
              {t('total')}
            </Text>
            <Text style={[styles.itemTextTotalProduct, { color: currentVenue?.appColor ?? '#001C47' }]}>
              {formattedTotalAmount}
            </Text>
          </View>
        </View>
      </View>

      <View style={{ flex: 1, width: mediumScreen ? '30%' : undefined }}>
        <View style={mediumScreen ? styles.orderDataLarge : styles.orderDataSmall}>
          <Text style={mediumScreen ? styles.sectionContainerTitleLarge : styles.sectionContainerTitleSmall}>
            {t('buyer-details')}
          </Text>

          <View
            style={
              mediumScreen
                ? [styles.containerLarge, order.isCash && !order.paymentId && { marginTop: '2%' }]
                : styles.containerSmall
            }
          >
            {!isParkingOrder && (
              <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
                <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>{t('delivery')}</Text>
                <Text style={mediumScreen ? styles.itemTextLarge : [styles.itemTextSmall, styles.deliveryTextSmall]}>
                  {order.delivery != null ? order.delivery : t('pickup')}
                </Text>
              </View>
            )}

            {order.deliveryTimeRange && (
              <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
                <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>{t('delivery-time-range')}</Text>
                <Text style={mediumScreen ? styles.itemTextLarge : styles.itemTextSmall}>{order.deliveryTimeRange || '-'}</Text>
              </View>
            )}

            {order.deliveryAddress && (
              <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
                <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>{t('delivery-address')}</Text>
                <Text style={mediumScreen ? styles.itemTextLarge : styles.itemTextSmall}>{order.deliveryAddress || '-'}</Text>
              </View>
            )}

            <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
              <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>{t('phone')}</Text>
              <Text style={mediumScreen ? styles.itemTextLarge : styles.itemTextSmall}>{order.phone || '-'}</Text>
            </View>

            {isParkingOrder && parkingVehicleData && (
              <>
                {parkingVehicleData[1] && (
                  <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
                    <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>{t('car-model')}</Text>
                    <Text style={mediumScreen ? styles.itemTextLarge : styles.itemTextSmall}>
                      {parkingVehicleData[1]}
                    </Text>
                  </View>
                )}
                <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
                  <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>
                    {t('license-plate')}
                  </Text>
                  <Text style={mediumScreen ? styles.itemTextLarge : styles.itemTextSmall}>
                    {parkingVehicleData[0]}
                  </Text>
                </View>
              </>
            )}

            <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
              <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>Email</Text>
              <Text style={mediumScreen ? styles.itemTextLarge : styles.itemTextSmall}>
                {order.buyer != null ? order.buyer.email : order.guestEmail}
              </Text>
            </View>
          </View>
        </View>

        <View style={mediumScreen ? styles.orderDataLarge : styles.orderDataSmall}>
          <Text style={mediumScreen ? styles.sectionContainerTitleLarge : styles.sectionContainerTitleSmall}>
            {t('order-details')}
          </Text>

          <View
            style={
              mediumScreen
                ? [styles.containerLarge, order.isCash && !order.paymentId && { marginTop: '2%' }]
                : styles.containerSmall
            }
          >
            <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
              <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>
                {t('confirmation-code')}
              </Text>
              <Text style={mediumScreen ? styles.itemTextLarge : styles.itemTextSmall}>{order.verificationCode}</Text>
            </View>

            {order.event && (
              <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
                <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>{t('event')}</Text>
                <Text style={mediumScreen ? styles.itemTextLarge : styles.itemTextSmall}>
                  {`${order.event.name} - ${formattedEventStartDate}`}
                </Text>
              </View>
            )}

            <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
              <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>{t('created-at')}</Text>
              <Text style={mediumScreen ? styles.itemTextLarge : styles.itemTextSmall}>
                {`${formattedCreatedDate}hs`}
              </Text>
            </View>

            <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
              <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>{t('payment-method')}</Text>
              <Text style={mediumScreen ? styles.itemTextLarge : styles.itemTextSmall}>
                {getPaymentMethodText(order)}
              </Text>
            </View>

            {order.isCash && !order.paymentId && (
              <View style={mediumScreen ? styles.dataRowLarge : styles.dataRowSmall}>
                <Text style={mediumScreen ? styles.headerTextLarge : styles.headerTextSmall}>
                  {t('pending-payment')}
                </Text>
              </View>
            )}

            <View style={mediumScreen ? {} : [styles.rowCtn]}>
              <TouchableOpacity onPress={openCommentsModal}>
                <Text>▶ {t('comment')}</Text>
              </TouchableOpacity>
            </View>

            {commentModal && (
              <CustomModal modalData={commentModal} onClose={() => setCommentModal(null)}>
                <Text>
                  {order.orderDetails && order.orderDetails.filter((detail) => detail.comments?.trim()).length > 0
                    ? order.orderDetails
                      .filter((detail) => detail.comments?.trim())
                      .map((detail) => `${detail.product.name}: ${detail.comments}`)
                      .join('\n')
                    : '-'}
                </Text>
              </CustomModal>
            )}
          </View>
        </View>
      </View>
    </ResponsiveContainer>
  );
};
